import { type Address, formatEther } from 'viem';
import type { MintSchedule } from '@soundxyz/sdk';
import { exhaustiveGuard } from '@soundxyz/utils';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import type {
  ContractType,
  EditionCollectableSchedulesInfoFragment,
  PaperCheckoutType,
} from '@graphql/reactQuery';
import {
  CreatePaperMerkleMinterV2KeyDocument,
  CreatePaperMinterAdapterKeyDocument,
  CreatePaperRangedEditionV2KeyDocument,
  CreatePaperSoundSwapKeyDocument,
  gql,
  useMutation,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { bigIntToNumber } from '@utils/bigNumberHelpers';
import { SoundContractInterfaceIds } from '@utils/constants';
import { ALLOWED_INTERFACES_FOR_CC, useIsCreditCardOnEditionAllowed } from '../credit-card-helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation CreatePaperSoundSwapKey($input: PaperSoundSwapSdkClientSecretInput!) {
    paperSoundSwapSdkClientSecret(input: $input) {
      __typename
      ... on MutationPaperSoundSwapSdkClientSecretSuccess {
        data
      }
      ... on Error {
        message
      }
    }
  }

  mutation CreatePaperMinterAdapterKey($input: PaperMinterAdapterSdkClientSecretInput!) {
    paperMinterAdapterSdkClientSecret(input: $input) {
      __typename
      ... on MutationPaperMinterAdapterSdkClientSecretSuccess {
        data
      }
      ... on Error {
        message
      }
    }
  }

  mutation CreatePaperMerkleMinterV2Key($input: PaperMerkleDropMinterV2SdkClientSecretInput!) {
    paperMerkleMinterV2SdkClientSecret(input: $input) {
      __typename
      ... on MutationPaperMerkleMinterV2SdkClientSecretSuccess {
        data
      }
      ... on Error {
        message
      }
    }
  }

  mutation CreatePaperRangedEditionV2Key($input: PaperRangedMinterV2SdkClientSecretInput!) {
    paperRangeMinterV2SdkClientSecret(input: $input) {
      __typename
      ... on MutationPaperRangeMinterV2SdkClientSecretSuccess {
        data
      }
      ... on Error {
        message
      }
    }
  }
`);

export function usePaperEditionCheckout({
  releaseId,
  releaseContractAddress,
  scheduleIds,
  schedule,
  chainId,
  quantity,
  type,
  auctionContractType,
  giftTo,
}: {
  releaseId: string;
  releaseContractAddress: string;
  scheduleIds: EditionCollectableSchedulesInfoFragment['scheduleIds'] | null | undefined;
  schedule: MintSchedule;
  chainId: number;
  quantity: number;
  type: PaperCheckoutType;
  auctionContractType: ContractType | null;
  giftTo: Address | null;
}) {
  const logError = useLogError();
  const logInfo = useLogInfo();
  const { activeMintSchedule, totalPriceEth } = useIsCreditCardOnEditionAllowed({
    releaseContractAddress,
    scheduleIds,
    schedule,
    chainId,
    quantity,
    auctionContractType,
  });

  const {
    mutateAsync: createPaperMinterAdpaterKey,
    isLoading: createPaperMinterAdapterKeyIsLoading,
  } = useMutation(CreatePaperMinterAdapterKeyDocument, {});
  const {
    mutateAsync: createPaperMerkleMinterV2Key,
    isLoading: createPaperMerkleMinterV2IsLoading,
  } = useMutation(CreatePaperMerkleMinterV2KeyDocument, {});
  const {
    mutateAsync: createPaperRangedEditionV2Key,
    isLoading: createPaperRangedEditionV2KeyIsLoading,
  } = useMutation(CreatePaperRangedEditionV2KeyDocument, {});

  const isLoading =
    createPaperMinterAdapterKeyIsLoading ||
    createPaperMerkleMinterV2IsLoading ||
    createPaperRangedEditionV2KeyIsLoading;

  if (!activeMintSchedule) {
    return {
      isLoading: true,
      getPaperKey: () => Promise.resolve({ error: 'No active mint schedule available' }),
    };
  }

  async function getPaperKey({
    affiliate,
  }: {
    affiliate: string;
  }): Promise<{ key: string; minterAddress: string } | { error: string }> {
    if (!activeMintSchedule) {
      return {
        error: 'No active mint schedule available',
      };
    }

    const mintId = activeMintSchedule.mintId;
    const totalPrice = totalPriceEth;
    const minterAddress = activeMintSchedule.minterAddress;

    const mintScheuleInterfaceId = activeMintSchedule.interfaceId;
    logInfo({
      action: MINTING_INFO_ACTIONS.GENERATE_PAPER_KEY,
      message: 'Generate paper key for release',
      data: {
        releaseId,
        totalPrice,
        minterAddress,
        quantity,
        mintId,
        type,
      },
      isArtistOnlyPage: false,
      pillar: PILLARS.MINTING,
    });

    try {
      switch (mintScheuleInterfaceId) {
        case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinter: {
          const {
            data: { paperMinterAdapterSdkClientSecret },
          } = await createPaperMinterAdpaterKey({
            input: {
              minter: activeMintSchedule.minterAddress,
              affiliate,
              releaseId,
              quantity,
              totalPrice,
              mintId: bigIntToNumber(mintId, { strict: true }),
              type,
              giftTo,
            },
          });

          if (
            paperMinterAdapterSdkClientSecret.__typename ===
            'MutationPaperMinterAdapterSdkClientSecretSuccess'
          ) {
            return {
              key: paperMinterAdapterSdkClientSecret.data,
              minterAddress,
            };
          } else {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: paperMinterAdapterSdkClientSecret.message,
              closeProcessingModal: true,
              indexedTags: {
                releaseId,
                mintId,
                type,
                quantity,
                totalPrice,
                minterAddress,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              message: 'Error creating paper key for IRangeEditionMinter',
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'error',
            });
            return {
              error:
                paperMinterAdapterSdkClientSecret.message ||
                'Something went wrong. Please try again.',
            };
          }
        }
        case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinterV2:
        case ALLOWED_INTERFACES_FOR_CC.IRangeEditionMinterV2_1: {
          const {
            data: { paperRangeMinterV2SdkClientSecret },
          } = await createPaperRangedEditionV2Key({
            input: {
              affiliate,
              quantity,
              mintId: bigIntToNumber(mintId, { strict: true }),
              releaseId,
              totalPrice,
              type,
              giftTo,
            },
          });

          if (
            paperRangeMinterV2SdkClientSecret.__typename ===
            'MutationPaperRangeMinterV2SdkClientSecretSuccess'
          ) {
            return { key: paperRangeMinterV2SdkClientSecret.data, minterAddress };
          } else {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: paperRangeMinterV2SdkClientSecret.message,
              closeProcessingModal: true,
              indexedTags: {
                releaseId,
                mintId,
                type,
                quantity,
                totalPrice,
                minterAddress,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              message: 'Error creating paper key for IRangeEditionMinterV2 & V2_1',
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'error',
            });
            return {
              error:
                paperRangeMinterV2SdkClientSecret.message ||
                'Something went wrong. Please try again.',
            };
          }
        }
        case ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2:
        case ALLOWED_INTERFACES_FOR_CC.IMerkleDropMinterV2_1: {
          if (activeMintSchedule.mintType !== 'MerkleDrop') {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: 'No merkle root found',
              message: 'No merkle root found',
              closeProcessingModal: true,
              indexedTags: {
                releaseId,
                mintId,
                type,
                quantity,
                totalPrice,
                minterAddress,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'error',
            });
            return {
              error: 'No merkle root found',
            };
          }
          const {
            data: { paperMerkleMinterV2SdkClientSecret },
          } = await createPaperMerkleMinterV2Key({
            input: {
              affiliate,
              quantity,
              mintId: bigIntToNumber(mintId, { strict: true }),
              merkleRoot: activeMintSchedule.merkleRoot,
              releaseId,
              totalPrice,
              type,
            },
          });

          if (
            paperMerkleMinterV2SdkClientSecret.__typename ===
            'MutationPaperMerkleMinterV2SdkClientSecretSuccess'
          ) {
            return { key: paperMerkleMinterV2SdkClientSecret.data, minterAddress };
          } else {
            logError({
              action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
              error: paperMerkleMinterV2SdkClientSecret.message,
              closeProcessingModal: true,
              indexedTags: {
                releaseId,
                mintId,
                type,
                quantity,
                totalPrice,
                minterAddress,
              },
              errorType: ERROR_TYPE.PAPER_CC,
              message: 'Error creating paper key for MerkleDropMinter',
              pillar: PILLARS.MINTING,
              isArtistOnlyPage: false,
              level: 'error',
            });
            return {
              error:
                paperMerkleMinterV2SdkClientSecret.message ||
                'Something went wrong. Please try again.',
            };
          }
        }
        // v1 merkle drop minter is not supported
        case SoundContractInterfaceIds.IMerkleDropMinter:
          logError({
            action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
            error: 'Unsupported contract for credit card checkout.',
            message: 'Unsupported contract for credit card checkout.',
            closeProcessingModal: true,
            indexedTags: {
              releaseId,
              mintId,
              type,
              quantity,
              totalPrice,
              minterAddress,
            },
            errorType: ERROR_TYPE.PAPER_CC,
            pillar: PILLARS.MINTING,
            isArtistOnlyPage: false,
            level: 'fatal',
          });

          return {
            error: 'Unsupported contract for credit card checkout.',
          };
        default: {
          exhaustiveGuard(mintScheuleInterfaceId);
        }
      }
    } catch (error) {
      logError({
        action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
        error,
        closeProcessingModal: true,
        indexedTags: {
          releaseId,
          mintId,
          type,
          quantity,
          totalPrice,
          minterAddress,
        },
        errorType: ERROR_TYPE.PAPER_CC,
        message: 'Error creating paper key',
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
        level: 'fatal',
      });

      return {
        error: 'Something went wrong. Please try again.',
      };
    }
  }

  return {
    getPaperKey,
    isLoading,
  };
}

export function usePaperSamCheckout({
  quantity,
  buyPriceWei,
  releaseId,
  type,
}: {
  releaseId: string;
  quantity: number;
  buyPriceWei: string;
  type: PaperCheckoutType;
}) {
  const { mutateAsync: createPaperSoundSwapKey, isLoading } = useMutation(
    CreatePaperSoundSwapKeyDocument,
    {},
  );

  async function getPaperKey({
    affiliate,
  }: {
    affiliate: string;
  }): Promise<{ key: string } | { error: string }> {
    const {
      data: { paperSoundSwapSdkClientSecret },
    } = await createPaperSoundSwapKey({
      input: {
        affiliate,
        releaseId,
        quantity,
        totalPrice: formatEther(BigInt(buyPriceWei)),
        type,
      },
    });

    if (
      paperSoundSwapSdkClientSecret.__typename === 'MutationPaperSoundSwapSdkClientSecretSuccess'
    ) {
      return { key: paperSoundSwapSdkClientSecret.data };
    } else {
      return {
        error: paperSoundSwapSdkClientSecret.message || 'Something went wrong. Please try again.',
      };
    }
  }

  return {
    getPaperKey,
    isLoading,
  };
}
